/**
 * GenericForm.tsx — a configuration form the Engine described.
 *
 * Every control here is chosen from the descriptor's own JSON-Schema fragment:
 * an `enum` becomes a select carrying the Engine's values and nothing else, a
 * number becomes a number input, an object or array becomes a JSON editor, and
 * anything the Engine did not describe becomes a text box. There is no list of
 * fields, no per-capability form, and no per-capability validation message
 * beyond the schema's own constraints.
 *
 * WHY THE UNPUBLISHED REMAINDER IS EDITABLE RATHER THAN HIDDEN
 * -------------------------------------------------------------
 * A schema with `additionalProperties: true` is the Engine saying "there are
 * properties here I have not described". Concealing that behind a form that
 * only offers the published subset would let an engineer believe a submission
 * was complete when it was not. So the form offers what was published, states
 * the remainder is Engine-validated, and lets the field through — the Engine
 * still decides whether it is acceptable, which is the only party entitled to.
 */

import type { ActionField, ActionPlan, FieldValues, ValueProblem } from "../../api/capabilityAction";

function problemFor(problems: readonly ValueProblem[], name: string): ValueProblem | undefined {
  return problems.find(problem => problem.field === name);
}

function Field({ field, value, problem, onChange, disabled }: {
  field: ActionField;
  value: string;
  problem: ValueProblem | undefined;
  onChange: (next: string) => void;
  disabled: boolean;
}): JSX.Element {
  const id = `field-${field.name}`;
  const describedBy = problem ? `${id}-problem` : undefined;

  // The Engine published a closed vocabulary for this field, so the form offers
  // exactly that vocabulary. A value outside it is not a thing the Engine
  // described, and inventing a wider control would invite exactly the request
  // it will refuse.
  if (field.options.length > 0 && !field.structured) {
    return (
      <>
        <label htmlFor={id}>{field.name}{field.required ? " *" : ""}</label>
        <select
          id={id} value={value} disabled={disabled || field.readOnly}
          aria-describedby={describedBy} aria-invalid={problem !== undefined}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">—</option>
          {field.options.map(option => <option key={option} value={option}>{option}</option>)}
        </select>
        {field.description && <p className="muted">{field.description}</p>}
        {problem && <p className="bad" id={`${id}-problem`}>{problem.detail}</p>}
      </>
    );
  }

  if (field.structured) {
    return (
      <>
        <label htmlFor={id}>{field.name}{field.required ? " *" : ""}</label>
        <textarea
          id={id} value={value} rows={4} spellCheck={false} disabled={disabled || field.readOnly}
          aria-describedby={describedBy} aria-invalid={problem !== undefined}
          placeholder={field.description || "JSON the Engine validates"}
          onChange={event => onChange(event.target.value)}
        />
        {field.description && <p className="muted">{field.description}</p>}
        {problem && <p className="bad" id={`${id}-problem`}>{problem.detail}</p>}
      </>
    );
  }

  const numeric = field.type === "number" || field.type === "integer";
  return (
    <>
      <label htmlFor={id}>{field.name}{field.required ? " *" : ""}</label>
      <input
        id={id} type={numeric ? "number" : field.type === "boolean" ? "checkbox" : "text"}
        checked={field.type === "boolean" ? value === "true" : undefined}
        value={field.type === "boolean" ? undefined : value}
        disabled={disabled || field.readOnly}
        readOnly={field.readOnly}
        aria-describedby={describedBy} aria-invalid={problem !== undefined}
        onChange={event => onChange(field.type === "boolean" ? String(event.target.checked) : event.target.value)}
      />
      {field.description && <p className="muted">{field.description}</p>}
      {field.readOnly && <p className="muted">The Engine marks this read-only. It is shown, never sent.</p>}
      {problem && <p className="bad" id={`${id}-problem`}>{problem.detail}</p>}
    </>
  );
}

export function GenericForm({ plan, values, problems, onChange, disabled }: {
  plan: ActionPlan;
  values: FieldValues;
  problems: readonly ValueProblem[];
  onChange: (values: FieldValues) => void;
  disabled?: boolean;
}): JSX.Element {
  if (plan.fields.length === 0) {
    return (
      <p className="muted">
        The Engine published no parameters for this capability, so there is nothing to configure. Sending it
        requires no payload from this browser.
      </p>
    );
  }
  return (
    <div className="generic-form">
      <p className="muted">
        These fields, their types and their permitted values come from the Engine&apos;s own schema for{" "}
        <code>{plan.descriptor.id}</code>. This browser validates nothing beyond what that schema states.
      </p>
      {plan.fields.map(field => (
        <div className="form-row" key={field.name}>
          <Field
            field={field}
            value={values[field.name] ?? ""}
            problem={problemFor(problems, field.name)}
            disabled={disabled ?? false}
            onChange={next => onChange({ ...values, [field.name]: next })}
          />
        </div>
      ))}
      {plan.derivedFields.length > 0 && (
        <p className="muted">
          Read-only by the Engine&apos;s own declaration: <code>{plan.derivedFields.join(", ")}</code>. These are
          derived from canonical state, and this browser neither offers nor sends them.
        </p>
      )}
    </div>
  );
}
